import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { trackLocal } from "@/lib/analytics";

const PwaContext = createContext(null);
export const usePwa = () => useContext(PwaContext);

const isIOSDevice = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent || "") &&
  !window.MSStream;

// On iOS, "Add to Home Screen" ONLY works in real Safari. Detect in-app browsers (Facebook, Instagram,
// Chrome-iOS, etc.) where the option is missing, so we can tell the user to open in Safari first.
const isInAppBrowser = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const ios = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
  if (/FBAN|FBAV|Instagram|Line\/|Twitter|Messenger|MicroMessenger|Snapchat|Pinterest/i.test(ua)) return true;
  // Real iOS Safari contains both "Safari" and "Version/". In-app WKWebviews / iOS Chrome (CriOS) do not.
  if (ios && !(/Safari/.test(ua) && /Version\//.test(ua))) return true;
  return false;
};

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true);

const SESSION_DISMISS_KEY = "inap_pwa_dismissed";
const WELCOME_KEY = "inap_pwa_welcomed";

export function PwaProvider({ children }) {
  const deferred = useRef(null);        // Android beforeinstallprompt event
  const [canInstall, setCanInstall] = useState(false);
  const [sheet, setSheet] = useState(null); // null | "prompt" | "ios" | "ios-open-safari" | "android" | "installed"
  const installed = isStandalone();
  const ios = isIOSDevice();
  const inApp = isInAppBrowser();

  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); deferred.current = e; setCanInstall(true); };
    const onInstalled = () => {
      deferred.current = null; setCanInstall(false);
      try { localStorage.setItem(WELCOME_KEY, "1"); } catch { /* ignore */ }
      trackLocal("pwa_install_accepted");
      setSheet("installed");  // Android: confirm right after the native install completes
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // First launch FROM the Home Screen (standalone) → one-time "you're all set" welcome.
  // This is the reliable cross-platform confirmation (iOS fires no install event).
  useEffect(() => {
    if (!installed) return;
    let welcomed = false;
    try { welcomed = !!localStorage.getItem(WELCOME_KEY); } catch { /* ignore */ }
    if (!welcomed) {
      try { localStorage.setItem(WELCOME_KEY, "1"); } catch { /* ignore */ }
      setSheet("installed");
    }
  }, [installed]);

  // Soft prompt shown once after a meaningful action (e.g. first result), per visit.
  const maybePrompt = useCallback(() => {
    if (installed) return;
    try { if (sessionStorage.getItem(SESSION_DISMISS_KEY)) return; } catch { /* ignore */ }
    setSheet("prompt");
    trackLocal("pwa_prompt_shown");
  }, [installed]);

  const dismiss = useCallback(() => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* ignore */ }
    setSheet(null);
    trackLocal("pwa_install_dismissed");
  }, []);

  const close = useCallback(() => setSheet(null), []);

  // "Save to Phone" button → native prompt on Android, Home Screen instructions on iPhone.
  const requestInstall = useCallback(async () => {
    trackLocal("pwa_save_clicked");
    if (deferred.current) {
      const evt = deferred.current;
      deferred.current = null;
      setCanInstall(false);
      try {
        evt.prompt();
        const choice = await evt.userChoice;
        if (choice?.outcome === "accepted") trackLocal("pwa_install_accepted");
        else trackLocal("pwa_install_dismissed");
      } catch { /* ignore */ }
      setSheet(null);
      return;
    }
    // No native prompt available → show platform instructions.
    if (ios) { setSheet("ios"); trackLocal("pwa_ios_instructions"); }
    else { setSheet("android"); }
  }, [ios]);

  // Opens the right flow on demand (menu / footer / homepage link).
  // iPhone → Home Screen instructions directly. Android/desktop → native install (or fallback).
  const openInstall = useCallback(() => {
    if (ios) { setSheet("ios"); trackLocal("pwa_ios_instructions"); }
    else { requestInstall(); }
  }, [ios, requestInstall]);

  return (
    <PwaContext.Provider value={{ canInstall, installed, ios, inApp, sheet, maybePrompt, openInstall, requestInstall, dismiss, close }}>
      {children}
    </PwaContext.Provider>
  );
}
